import { computed, effect, untracked } from '@angular/core';
import { dataFromState } from '@libs/core/lib/state/async-states';
import { withGetProductById } from '@libs/mtjr/shared/generated/entities/product/product-store-features';
import { Entity, Enums } from '@libs/mtjr/shared/generated/types';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { flatMap, groupBy, isEmpty, uniqWith } from 'lodash-es';

type ProductDetailsState = {
  selectedImageIndex: number;
  selectedAttributes: Entity.VariationAttribute[];
};

export const ProductDetailsStore = signalStore(
  withState<ProductDetailsState>({
    selectedImageIndex: 0,
    selectedAttributes: [],
  }),
  withGetProductById(),

  withComputed((store) => ({
    product: dataFromState(store.getProductByIdState, (data) => data),
  })),

  withHooks((store) => ({
    onInit() {
      effect(() => {
        const variations = store.product()?.variations ?? [];
        if (isEmpty(variations)) return;
        const allAttrs = variations
          .filter((v) => v.stock > 0)
          .map((v) => v.attributes)
          .flat();
        if (isEmpty(allAttrs)) return;
        const attrs = uniqWith(allAttrs, (a, b) => a.type === b.type);
        untracked(() => {
          patchState(store, { selectedAttributes: attrs });
        });
      });
    },
  })),
  withMethods((store) => ({
    setSelectedImage(index: number) {
      patchState(store, { selectedImageIndex: index });
    },
    toggleAttribute(attr: Entity.VariationAttribute) {
      const selected = store.selectedAttributes().filter((x) => x.typeKey !== attr.typeKey);
      const index = selected.findIndex((x) => x.key === attr.key);
      if (index === -1) {
        patchState(store, { selectedAttributes: [...selected, attr] });
      } else {
        selected.splice(index, 1);
        patchState(store, { selectedAttributes: selected });
      }
    },
  })),
  withComputed((store) => ({
    isVoucherVendor: computed(() => {
      const product = store.product();
      return product?.vendor.vendorType === 'voucher';
    }),
    selectedVariation: computed(() => {
      const selectedAttrs = store.selectedAttributes();
      const variations = store.product()?.variations ?? [];

      if (variations[0]?.id === '000000000000000000000000') return variations[0];

      if (isEmpty(variations) || isEmpty(selectedAttrs)) return undefined;
      return variations.find((v) =>
        v.attributes.every((a) =>
          selectedAttrs.some((s) => {
            return a.key === s.key && a.typeKey === s.typeKey;
          }),
        ),
      );
    }),

    attributes: computed(() => {
      const state = store.getProductByIdState();
      if (!state.success) return [];
      const variations = state.data.variations;
      const attrs: Entity.VariationAttribute[] = uniqWith(
        flatMap(variations, (x) => x.attributes),
        (a, b) => a.key === b.key && a.typeKey === b.typeKey,
      );
      const varAttr: AttributeSection[] = [];
      const grouped = groupBy(attrs, 'typeKey');
      for (const key in grouped) {
        const values = grouped[key];
        if (isEmpty(values)) continue;
        varAttr.push({
          key: key,
          label: values[0].typeKey,
          type: values[0].type,
          values: values,
        });
      }
      return varAttr;
    }),
    // attributes that are available in at least one variation
    availableAttributes: computed(() => {
      const state = store.getProductByIdState();
      if (!state.success) return [];
      const variations = state.data.variations;
      let availableAttrs: string[] = [];
      for (const variation of variations ?? []) {
        if (variation.stock > 0) {
          availableAttrs = [...availableAttrs, ...variation.attributes.map((attr) => `${attr.typeKey}-${attr.key}`)];
        }
      }
      return availableAttrs;
    }),
  })),

  withComputed((store) => ({
    dynamicInfo: computed(() => {
      const product = store.product();
      if (!product) {
        return {
          price: 0,
          images: ['https://nbg1.your-objectstorage.com/mtjr-prod/product-placeholder.webp'],
          stock: 0,
          available: false,
          blurHash: 'LEQ0XHof~qof%MayRjj[-;fQIUay',
        };
      }
      const variant = store.selectedVariation();
      const variationsImages = product.variations?.map((v) => v.image).filter((x) => x);
      const stock = product.stock ?? (product.variations != null ? variant?.stock : 0) ?? 0;
      return {
        price: product.price ?? variant?.price,
        images: [...(product.images ?? []), ...(variationsImages ?? [])].filter((x): x is string => !!x),
        blurHash: product.blurHash,
        stock,
        available: stock > 0,
      };
    }),
  })),

  withComputed((store) => ({
    selectedImage: computed(
      () =>
        store.dynamicInfo().images[store.selectedImageIndex()] ?? 'https://nbg1.your-objectstorage.com/mtjr/logo.svg',
    ),
  })),
);

type AttributeSection = {
  key: string;
  label: string;
  type: Enums.AttributeType;
  values: Entity.VariationAttribute[];
};
